import React, { useEffect, useState } from "react";
import { Patient } from "../../PatientCard";
import VitalSignsModal from "./VitalSignsModal";
import VitalSignsHistory from "./VitalSignsHistory";
import getIcon from "../../../../utils/Icons";
import VitalSignsService, {
  PatientVitalSigns,
} from "../../../../services/vitalService";

interface PatientVitalSignsTabProps {
  patient: Patient;
}

const PatientVitalSignsTab: React.FC<PatientVitalSignsTabProps> = ({
  patient,
}) => {
  const [showVitalSignsModal, setShowVitalSignsModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const handleVitalSignsRecorded = (vitalSignsId: number) => {
    // alert("Vital signs recorded successfully!");
    setShowVitalSignsModal(false);
    setRefreshKey((prev) => prev + 1);
  };
  const [latestVitalSigns, setLatestVitalSigns] =
    useState<PatientVitalSigns | null>(null);
  const [vitalSignsData, setVitalSignsData] = useState<PatientVitalSigns[]>([]);
  const [summaryStats, setSummaryStats] = useState({
    totalRecords: 0,
    normalReadings: 0,
    abnormalReadings: 0,
    lastMeasurement: "--",
  });

  useEffect(() => {
    const loadVitalSignsData = async () => {
      try {
        const result = await VitalSignsService.getPatientVitalSigns(
          patient.id,
          50
        );
        setVitalSignsData(result);

        // Get latest vital signs for the display cards
        const latest = await VitalSignsService.getLatestVitalSigns(patient.id);
        setLatestVitalSigns(latest);

        // Calculate summary statistics
        const totalRecords = result.length;
        const normalReadings = result.filter(
          (vs) => vs.assessments?.overall?.status === "Normal"
        ).length;
        const abnormalReadings = result.filter(
          (vs) => vs.has_abnormal_values
        ).length;
        const lastMeasurement =
          totalRecords > 0
            ? result[0].time_since_measurement ||
              (result[0].measurement_info?.measured_at
                ? VitalSignsService.formatDateTime(
                    result[0].measurement_info.measured_at
                  )
                : result[0].created_at
                ? VitalSignsService.formatDateTime(result[0].created_at)
                : "Unknown")
            : "--";

        setSummaryStats({
          totalRecords,
          normalReadings,
          abnormalReadings,
          lastMeasurement,
        });
      } catch (error) {
        console.error("Failed to load vital signs data:", error);
      }
    };

    if (patient.id) {
      loadVitalSignsData();
    }
  }, [patient.id, refreshKey]);

  const containerStyle: React.CSSProperties = {
    padding: "16px",
    height: "100%",
    overflow: "auto",
    maxWidth: "100%",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "24px",
    padding: "16px",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    border: "1px solid #e5e7eb",
  };

  const headerContentStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "12px",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => ({
    padding: "12px 20px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#3b82f6" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    minWidth: "140px",
    whiteSpace: "nowrap",
  });

  const vitalCardStyle: React.CSSProperties = {
    padding: "16px",
    background: "#f8fafc",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    minWidth: "0", // Allow cards to shrink
    flex: "1",
  };

  const statsCardStyle: React.CSSProperties = {
    textAlign: "center" as const,
    minWidth: "120px",
    flex: "1",
  };

  return (
    <>
      <div style={containerStyle}>
        {/* Header with Action Buttons */}
        <div style={headerStyle}>
          <div style={headerContentStyle} className="nn">
            <div style={{ flex: "1", minWidth: "200px" }}>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  margin: 0,
                  marginBottom: "4px",
                  color: "#1f2937",
                }}
              >
                {getIcon("stethoscope")} Vital Signs
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  margin: 0,
                  wordBreak: "break-word",
                }}
              >
                Record and monitor vital signs for{" "}
                {patient.full_name ||
                  `${patient.first_name} ${patient.last_name}`}
              </p>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
              <button
                style={buttonStyle("primary")}
                onClick={() => setShowVitalSignsModal(true)}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#2563eb")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#3b82f6")
                }
              >
                {getIcon("stethoscope")} Record Vitals
              </button>
            </div>
          </div>
        </div>

        {/* Patient Health Alerts */}
        {(patient.allergies?.length || patient.chronic_conditions?.length) && (
          <div
            style={{
              background: "#fef3c7",
              border: "1px solid #f59e0b",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <h4
              style={{
                color: "#92400e",
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              {getIcon("warning")} Important Medical Information
            </h4>
            {patient.allergies?.length && (
              <div
                style={{
                  color: "#92400e",
                  fontSize: "13px",
                  marginBottom: "4px",
                  wordBreak: "break-word",
                }}
              >
                <strong>Allergies:</strong> {patient.allergies.join(", ")}
              </div>
            )}
            {patient.chronic_conditions?.length && (
              <div
                style={{
                  color: "#92400e",
                  fontSize: "13px",
                  wordBreak: "break-word",
                }}
              >
                <strong>Chronic Conditions:</strong>{" "}
                {patient.chronic_conditions.join(", ")}
              </div>
            )}
          </div>
        )}

        {/* Vital Signs Recording Modal */}
        <VitalSignsModal
          patient={patient}
          isOpen={showVitalSignsModal}
          onClose={() => setShowVitalSignsModal(false)}
          onVitalSignsRecorded={handleVitalSignsRecorded}
        />

        {/* Vital Signs History */}
        <VitalSignsHistory
          patient={patient}
          isOpen={true}
          onClose={() => {}}
          key={refreshKey}
        />

        {/* Latest Vital Signs Display */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "16px",
            marginTop: "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e5e7eb",
          }}
        >
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1f2937",
            }}
          >
            {getIcon("stethoscope")} Latest Vital Signs
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: "12px",
            }}
          >
            {/* Blood Pressure Card */}
            <div style={vitalCardStyle}>
              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                }}
              >
                Blood Pressure
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color:
                    latestVitalSigns?.assessments?.blood_pressure?.color ||
                    "#1e293b",
                  wordBreak: "break-word",
                }}
              >
                {latestVitalSigns?.display_values?.blood_pressure ||
                  (latestVitalSigns?.vital_signs?.systolic_bp &&
                  latestVitalSigns?.vital_signs?.diastolic_bp
                    ? `${latestVitalSigns.vital_signs.systolic_bp}/${latestVitalSigns.vital_signs.diastolic_bp} mmHg`
                    : "--/-- mmHg")}
              </div>
              <div style={{ fontSize: "11px", color: "#64748b" }}>
                {latestVitalSigns?.assessments?.blood_pressure?.status ||
                  "Not recorded"}
              </div>
            </div>

            {/* Heart Rate Card */}
            <div style={vitalCardStyle}>
              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                }}
              >
                Heart Rate
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color:
                    latestVitalSigns?.assessments?.heart_rate?.color ||
                    "#1e293b",
                }}
              >
                {latestVitalSigns?.display_values?.heart_rate ||
                  (latestVitalSigns?.vital_signs?.heart_rate
                    ? `${latestVitalSigns.vital_signs.heart_rate} bpm`
                    : "-- bpm")}
              </div>
              <div style={{ fontSize: "11px", color: "#64748b" }}>
                {latestVitalSigns?.assessments?.heart_rate?.status ||
                  "Not recorded"}
              </div>
            </div>

            {/* Temperature Card */}
            <div style={vitalCardStyle}>
              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                }}
              >
                Temperature
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color:
                    latestVitalSigns?.assessments?.temperature?.color ||
                    "#1e293b",
                }}
              >
                {latestVitalSigns?.display_values?.temperature ||
                  (latestVitalSigns?.vital_signs?.temperature
                    ? `${latestVitalSigns.vital_signs.temperature}°C`
                    : "--°C")}
              </div>
              <div style={{ fontSize: "11px", color: "#64748b" }}>
                {latestVitalSigns?.assessments?.temperature?.status ||
                  "Not recorded"}
              </div>
            </div>

            {/* Oxygen Saturation Card */}
            <div style={vitalCardStyle}>
              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginBottom: "4px",
                }}
              >
                Oxygen Saturation
              </div>
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color:
                    latestVitalSigns?.assessments?.oxygen_saturation?.color ||
                    "#1e293b",
                }}
              >
                {latestVitalSigns?.display_values?.oxygen_saturation ||
                  (latestVitalSigns?.vital_signs?.oxygen_saturation
                    ? `${latestVitalSigns.vital_signs.oxygen_saturation}%`
                    : "--%")}
              </div>
              <div style={{ fontSize: "11px", color: "#64748b" }}>
                {latestVitalSigns?.assessments?.oxygen_saturation?.status ||
                  "Not recorded"}
              </div>
            </div>
          </div>

          {/* Additional vital signs if available */}
          {latestVitalSigns && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                gap: "12px",
                marginTop: "12px",
              }}
            >
              {/* Weight Card */}
              {latestVitalSigns.vital_signs?.weight && (
                <div style={vitalCardStyle}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      marginBottom: "4px",
                    }}
                  >
                    Weight
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "#1e293b",
                    }}
                  >
                    {latestVitalSigns.display_values?.weight ||
                      `${latestVitalSigns.vital_signs.weight} kg`}
                  </div>
                  {latestVitalSigns.vital_signs?.bmi && (
                    <div style={{ fontSize: "11px", color: "#64748b" }}>
                      BMI: {latestVitalSigns.vital_signs.bmi} kg/m² (
                      {latestVitalSigns.assessments?.bmi?.category || "Unknown"}
                      )
                    </div>
                  )}
                </div>
              )}

              {/* Pain Scale Card */}
              {latestVitalSigns.vital_signs?.pain_scale !== undefined && (
                <div style={vitalCardStyle}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      marginBottom: "4px",
                    }}
                  >
                    Pain Level
                  </div>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color:
                        latestVitalSigns.assessments?.pain?.color || "#1e293b",
                    }}
                  >
                    {latestVitalSigns.vital_signs.pain_scale}/10
                  </div>
                  <div style={{ fontSize: "11px", color: "#64748b" }}>
                    {latestVitalSigns.assessments?.pain?.level || "Pain level"}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Last measurement time */}
          {latestVitalSigns && (
            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                background: "#f0f9ff",
                border: "1px solid #dbeafe",
                borderRadius: "6px",
                fontSize: "13px",
                color: "#1e40af",
                textAlign: "center",
                wordBreak: "break-word",
              }}
            >
              📅 Last measured:{" "}
              {latestVitalSigns.time_since_measurement ||
                (latestVitalSigns.measurement_info?.measured_at
                  ? VitalSignsService.formatDateTime(
                      latestVitalSigns.measurement_info.measured_at
                    )
                  : latestVitalSigns.created_at
                  ? VitalSignsService.formatDateTime(
                      latestVitalSigns.created_at
                    )
                  : "Unknown time")}
            </div>
          )}

          {/* No data message */}
          {!latestVitalSigns && (
            <div
              style={{
                textAlign: "center",
                padding: "32px 16px",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              📊 No vital signs recorded yet
              <br />
              <span style={{ fontSize: "12px" }}>
                Click "Record Vitals" to add the first measurement
              </span>
            </div>
          )}
        </div>

        {/* Vital Signs Statistics */}
        <div
          style={{
            background: "#ffffff",
            borderRadius: "12px",
            padding: "16px",
            marginTop: "24px",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
            border: "1px solid #e5e7eb",
          }}
        >
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "16px",
              color: "#1f2937",
            }}
          >
            {getIcon("trending-up")} Vital Signs Summary
          </h4>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: "16px",
            }}
          >
            <div style={statsCardStyle}>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#3b82f6",
                  marginBottom: "4px",
                }}
              >
                {summaryStats.totalRecords}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                Total Records
              </div>
            </div>

            <div style={statsCardStyle}>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#10b981",
                  marginBottom: "4px",
                }}
              >
                {summaryStats.normalReadings}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                Normal Readings
              </div>
            </div>

            <div style={statsCardStyle}>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#f59e0b",
                  marginBottom: "4px",
                }}
              >
                {summaryStats.abnormalReadings}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                Abnormal Readings
              </div>
            </div>

            <div style={statsCardStyle}>
              <div
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: "#6b7280",
                  marginBottom: "4px",
                }}
              >
                {summaryStats.lastMeasurement.includes("ago") ? "📅" : "--"}
              </div>
              <div style={{ fontSize: "12px", color: "#6b7280" }}>
                Last Measurement
              </div>
              {summaryStats.lastMeasurement !== "--" && (
                <div
                  style={{
                    fontSize: "10px",
                    color: "#9ca3af",
                    marginTop: "2px",
                    wordBreak: "break-word",
                  }}
                >
                  {summaryStats.lastMeasurement}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Panel */}
        {/* <div
          style={{
            background: "#f0f9ff",
            border: "1px solid #dbeafe",
            borderRadius: "8px",
            padding: "16px",
            marginTop: "24px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "20px", marginBottom: "8px" }}>
            {getIcon("stethoscope")}
          </div>
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#1e40af",
            }}
          >
            Complete Vital Signs Management
          </h4>
          <p
            style={{
              fontSize: "14px",
              color: "#1e40af",
              marginBottom: "16px",
              lineHeight: "1.5",
            }}
          >
            Record and monitor patient vital signs including blood pressure,
            heart rate, temperature, oxygen saturation, weight, and pain levels.
            Track trends and receive alerts for abnormal values.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "8px",
              fontSize: "12px",
              color: "#1e40af",
            }}
          >
            <div>{getIcon("success")} Blood Pressure Monitoring</div>
            <div>{getIcon("success")} Heart Rate & Pulse</div>
            <div>{getIcon("success")} Temperature Tracking</div>
            <div>{getIcon("success")} Oxygen Saturation</div>
            <div>{getIcon("success")} Weight & BMI</div>
            <div>{getIcon("success")} Pain Scale Assessment</div>
          </div>
        </div> */}
      </div>

      <style>
        {`
          @media (max-width: 768px) {
            .vital-card {
              min-width: 140px;
            }
            
            .header-content {
              flex-direction: column;
              align-items: stretch;
            }
            
            .button-group {
              justify-content: center;
            }
            
            .stats-grid {
              grid-template-columns: repeat(2, 1fr);
            }
          }
          
          @media (max-width: 480px) {
            .vital-signs-container {
              padding: 12px;
            }
            
            .vital-card {
              min-width: 120px;
            }
            
            .stats-grid {
              grid-template-columns: 1fr;
            }
            
            .info-features {
              grid-template-columns: 1fr;
            }
          }
        `}
      </style>
    </>
  );
};

export default PatientVitalSignsTab;
